// ============================================================
// TopBar — Square Code shell brand + panel mode switcher + mode pill.
//
// Editorial brand: SQUARE CODE kicker + "mainframe shell" micro label.
// Panel mode controls are tight chip buttons that follow the reference
// "command center" rhythm — small, spaced, decisive.
// ============================================================

import type { CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import { useShell, type PanelMode } from './ShellContext'
import { getRuntime } from '../hooks/_mode'
import FeedbackTrigger from '../observability/FeedbackTrigger'
import { SHELL_TOKENS, shellStyles, text } from './styles'

export default function TopBar() {
  const { panelMode, setPanelMode } = useShell()
  const { live } = getRuntime()
  const location = useLocation()

  // Panel mode buttons are contextual — only show on routes where the
  // right panel provides real value (OLIVIUM, dashboard, command center,
  // project detail). Hide on workspace creation surfaces and settings
  // where they do nothing and create confusion.
  const panelRoutes = [
    '/square/dashboard',
    '/square/olivium/',
    '/square/command-center/',
    '/square/control',
  ]
  const isProjectDetail = /^\/square\/projects\/[^/]+$/.test(location.pathname)
  const showPanelControls =
    isProjectDetail ||
    panelRoutes.some((r) => location.pathname.startsWith(r))

  return (
    <header style={shellStyles.topbar}>
      {/* Brand cluster */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #1a1028, #0d0a14)',
            border: `1px solid ${SHELL_TOKENS.brandLine}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 16px ${SHELL_TOKENS.brandGlow}`,
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 900,
              color: SHELL_TOKENS.brand,
              textShadow: `0 0 10px ${SHELL_TOKENS.brandGlow}`,
              fontFamily: 'var(--font-brand)',
            }}
          >
            Q
          </span>
        </div>
        <div>
          <div
            style={{
              ...text.kicker,
              color: SHELL_TOKENS.inkFaint,
              fontSize: 9,
              letterSpacing: '0.24em',
              marginBottom: 2,
            }}
          >
            SQUARE CODE
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: SHELL_TOKENS.ink,
              letterSpacing: '-0.005em',
              fontFamily: 'var(--font-brand)',
            }}
          >
            Command Center
          </div>
        </div>
      </div>

      {/* Right cluster: panel switch + feedback + mode pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {showPanelControls && (
          <>
            <PanelButton mode="detail" current={panelMode} onSelect={setPanelMode}>
              Detail
            </PanelButton>
            <PanelButton mode="comms" current={panelMode} onSelect={setPanelMode}>
              Comms
            </PanelButton>
            <PanelButton mode="action" current={panelMode} onSelect={setPanelMode}>
              Action
            </PanelButton>

            <div style={{ width: 1, height: 22, background: SHELL_TOKENS.borderAlt, margin: '0 4px' }} />
          </>
        )}

        <FeedbackTrigger />

        <div
          style={{
            minWidth: 56,
            textAlign: 'center',
            borderRadius: 999,
            padding: '5px 10px',
            fontWeight: 800,
            fontSize: 9,
            letterSpacing: '0.16em',
            border: live
              ? '1px solid rgba(5,150,105,0.32)'
              : '1px solid rgba(245,158,11,0.32)',
            background: live ? 'rgba(5,150,105,0.10)' : 'rgba(245,158,11,0.10)',
            color: live ? SHELL_TOKENS.greenText : SHELL_TOKENS.amberText,
            marginLeft: 4,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {live ? 'LIVE' : 'DEMO'}
        </div>
      </div>
    </header>
  )
}

function PanelButton({
  mode,
  current,
  onSelect,
  children,
}: {
  mode: PanelMode
  current: PanelMode
  onSelect: (m: PanelMode) => void
  children: React.ReactNode
}) {
  const active = mode === current
  const style: CSSProperties = {
    border: active ? `1px solid ${SHELL_TOKENS.brandLine}` : `1px solid ${SHELL_TOKENS.border}`,
    background: active ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surfaceHigh,
    color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkDim,
    borderRadius: 8,
    padding: '5px 11px',
    cursor: 'pointer',
    fontWeight: active ? 700 : 600,
    fontSize: 10,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontFamily: 'var(--font-mono)',
  }
  return (
    <button type="button" onClick={() => onSelect(mode)} style={style}>
      {children}
    </button>
  )
}
